import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import ReactSelect from "components/ReactSelect";
import { useAuth } from "hooks/useAuth";

export default function Filter({ filters, setFilters }) {
  const [currentFilters, setCurrentFilters] = useState({
    ...filters,
    status: filters?.status?.split(","),
  });
  const [agents, setAgents] = useState([]);
  const [formKey, setFormKey] = useState(Date.now());
  const { authToken } = useAuth();

  const handleChange = (event) => {
    if (event.target.name === "status") {
      let filter = currentFilters[event.target.name];
      if (!filter) {
        filter = [event.target.value];
      } else {
        if (event.target.checked) {
          filter.push(event.target.value);
        } else {
          const idx = filter.indexOf(event.target.value);
          filter.splice(idx, 1);
        }
      }
      setCurrentFilters({ ...currentFilters, [event.target.name]: filter });
    } else if (event.target.value.length === 0) {
      const newFilters = currentFilters;
      delete newFilters[event.target.name];
      setCurrentFilters({ ...newFilters });
    } else {
      setCurrentFilters({
        ...currentFilters,
        [event.target.name]: event.target.value,
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFilters({
      ...currentFilters,
      status: currentFilters?.status?.join(","),
    });
  };

  const handleClear = () => {
    setFilters({});
    setCurrentFilters({});
    setFormKey(Date.now());
  };

  const getErtMembers = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}virtual-team/members/ert_team_members/`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => {
        let members = response.data?.map((member) => {
          return {
            label: `${member?.account_id__first_name} ${member?.account_id__last_name}`,
            value: member.account_id,
          };
        });
        setAgents(members);
      })
      .catch((err) => console.log("unable to get ert team members", err));
  }, []);

  useEffect(() => {
    getErtMembers();
  }, [getErtMembers]);

  return (
    <Form onSubmit={handleSubmit} id="filters" key={formKey}>
      <Form.Group className="d-inline-block" controlId="from-date">
        <Form.Label>Time Frame</Form.Label>
        <Form.Control type="date" name="from_date" onChange={handleChange} />
      </Form.Group>
      <p className="d-inline-block p-3">to</p>
      <Form.Group className="d-inline-block" controlId="to-date">
        <Form.Label>Time Frame</Form.Label>
        <Form.Control type="date" name="to_date" onChange={handleChange} />
      </Form.Group>
      <Form.Group controlId="date-type" className="mb-3">
        <Form.Label>For The</Form.Label>
        <Form.Select name="date_type" onChange={handleChange}>
          <option value="">Select a type for the time frame</option>
          <option value="entrydate">Entry Date</option>
          <option value="moddate">Modification Date</option>
          <option value="release_date">Escalation Date</option>
          <option value="due">Deadline</option>
          <option value="enddate">Completion or Cancellation Date</option>
        </Form.Select>
      </Form.Group>
      <Form.Group controlId="just" className="mb-3">
        <Form.Label>General Text:</Form.Label>
        <Form.Control type="text" name="just" onChange={handleChange} />
      </Form.Group>
      <Row>
        <Form.Label>VID Range</Form.Label>
        <Col>
          <Form.Group controlId="vid_start" className="mb-3">
            <Form.Label className="visually-hidden">
              Minimum VID Range Value
            </Form.Label>
            <Form.Control
              type="number"
              name="vid_start"
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
        -
        <Col>
          <Form.Group controlId="vid_end" className="mb-3">
            <Form.Label className="visually-hidden">
              Maximum VID Range Value
            </Form.Label>
            <Form.Control
              type="number"
              name="vid_end"
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group controlId="status" className="mb-3">
        <Form.Label>Status</Form.Label>
        <Row>
          <Col lg={6} xs={12}>
            <Form.Check
              type="checkbox"
              name="status"
              value="Draft"
              label="Draft"
              onChange={handleChange}
              id="status1"
            />
          </Col>
          <Col lg={6} xs={12}>
            <Form.Check
              type="checkbox"
              name="status"
              value="Pending Review"
              label="Pending Review"
              onChange={handleChange}
              defaultChecked={currentFilters.status?.some(
                (status) => status === "Pending Review",
              )}
              id="status2"
            />
          </Col>
        </Row>
        <Row>
          <Col lg={6} xs={12}>
            <Form.Check
              type="checkbox"
              name="status"
              value="Completed - Approved with Provisions"
              label="Completed - Approved with Provisions"
              onChange={handleChange}
              defaultChecked={currentFilters.status?.some(
                (status) => status === "Completed - Approved with Provisions",
              )}
              id="status3"
            />
          </Col>
          <Col lg={6} xs={12}>
            <Form.Check
              type="checkbox"
              name="status"
              value="Completed - Approved to Proceed"
              label="Completed - Approved to Proceed"
              onChange={handleChange}
              defaultChecked={currentFilters.status?.some(
                (status) => status === "Completed - Approved to Proceed",
              )}
              id="status4"
            />
          </Col>
        </Row>
        <Row>
          <Col lg={6} xs={12}>
            <Form.Check
              type="checkbox"
              name="status"
              value="Completed - EAR Previously Approved"
              label="Completed - EAR Previously Approved"
              onChange={handleChange}
              defaultChecked={currentFilters.status?.some(
                (status) => status === "Completed - EAR Previously Approved",
              )}
              id="status5"
            />
          </Col>
          <Col lg={6} xs={12}>
            <Form.Check
              type="checkbox"
              name="status"
              value="Completed - No EAR Required"
              label="Completed - No EAR Required"
              onChange={handleChange}
              defaultChecked={currentFilters.status?.some(
                (status) => status === "Completed - No EAR Required",
              )}
              id="status6"
            />
          </Col>
        </Row>
        <Row>
          <Col lg={6} xs={12}>
            <Form.Check
              type="checkbox"
              name="status"
              value="Rejected - Pending Revision"
              label="Rejected - Pending Revision"
              onChange={handleChange}
              defaultChecked={currentFilters.status?.some(
                (status) => status === "Rejected - Pending Revision",
              )}
              id="status7"
            />
          </Col>
        </Row>
      </Form.Group>
      <Form.Group controlId="agents" className="mb-3">
        <Form.Label>Agent</Form.Label>
        <ReactSelect
          id="agents"
          name="agents"
          handleChange={handleChange}
          defaultValue={agents.filter((agent) =>
            currentFilters?.agents?.includes(agent.value),
          )}
          options={agents}
          newObj={currentFilters}
          isMulti={true}
          isValid={true}
        />
      </Form.Group>
      <div className="mb-3">
        <Button type="submit" variant="primary" onClick={handleSubmit}>
          Apply
        </Button>
        &nbsp;
        <Button type="reset" variant="outline-dark" onClick={handleClear}>
          Clear
        </Button>
      </div>
    </Form>
  );
}
